import { SyntheticEvent, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import UPDATE_VIDEO_MUTATION from '@/graphql/mutations/update-video.graphql';

export type ClientVideo = {
  timesOpened: number;
  durationElapsed: number;
  isCompleted: boolean;
} | null;

/**
 * Reports watch progress for a video: `onTimeUpdate` saves every 10 seconds and on
 * completion, `registerOpen` bumps `timesOpened` once per viewing.
 */
const useVideoProgress = (videoId: number, clientVideo: ClientVideo) => {
  const [updateVideo] = useMutation(UPDATE_VIDEO_MUTATION);

  const registerOpen = useCallback(() => updateVideo({
    variables: {
      clientVideo: {
        videoId,
        isCompleted: Boolean(clientVideo?.isCompleted),
        timesOpened: (clientVideo?.timesOpened ?? 0) + 1,
        durationElapsed: clientVideo?.durationElapsed ?? 0,
      }
    }
  }), [videoId, clientVideo, updateVideo]);

  const onTimeUpdate = useCallback(
    async (event: SyntheticEvent<HTMLVideoElement, Event>) => {
      const video = event.target as HTMLVideoElement;
      const exactTime = video.currentTime;
      const currentTime = Math.floor(exactTime);
      const duration = Math.floor(video.duration);
      const isCompleted = currentTime === duration;

      const input = {
        videoId,
        isCompleted: Boolean(clientVideo?.isCompleted ?? isCompleted),
        timesOpened: clientVideo?.timesOpened ?? 1,
        durationElapsed: (clientVideo?.durationElapsed ?? 0) + currentTime,
      };

      if (currentTime < 1) return;

      if (isCompleted || currentTime % 10 === 0) {
        const updated: any = await updateVideo({ variables: { clientVideo: input } });
        return updated;
      }
    }, [videoId, clientVideo, updateVideo]);

  return { onTimeUpdate, registerOpen };
};

export default useVideoProgress;
